import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { answerConditionIntakeFormQuestion } from '@api';
import {
  cn,
  ErrorMessage,
  Field,
  Label,
  Paragraph,
  Radio,
  RadioGroup,
  Textarea,
} from '@quickmd-llc/design-system';
import { ConditionQuestion } from '@qmd-types/intakeForms';
import { SubmitButton } from './SubmitButton';
import { useIntakeForm } from '../../context/useIntakeForm';

type Props = {
  question: ConditionQuestion;
};

type FormValues = {
  inputValue: string | null;
  option?: string;
};

const INPUT_MAX_LENGTH = 500;

function buildDefaultValues(question: ConditionQuestion): FormValues {
  const selectedOption = question.options.find((option) => option.isSelected);

  return {
    inputValue: selectedOption?.input?.value || null,
    option: selectedOption?.name,
  };
}

export function ConditionForm({ question }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { handleNextStep, intakeForm, updateAnswer } = useIntakeForm();
  const {
    clearErrors,
    control,
    handleSubmit,
    formState,
    register,
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: buildDefaultValues(question),
  });
  const { options } = question;
  const { errors } = formState;

  const option = watch('option');
  const inputValue = watch('inputValue');

  const conditionalInput = useMemo(
    () => options.find(({ name }) => name === option)?.input,
    [option, options],
  );

  async function onSubmit(data: FormValues) {
    if (!data.option) {
      return handleNextStep();
    }

    const { intakeFormQuestionId } = question;
    const { intakeFormId } = intakeForm;

    setIsLoading(true);

    try {
      await answerConditionIntakeFormQuestion({
        intakeFormQuestionId,
        intakeFormId,
        data: {
          option: data.option,
          inputValue: data.inputValue,
        },
      });

      const updatedOptions = options.map((opt) => {
        if (opt.name === data.option) {
          const input = opt.input && {
            ...opt.input,
            value: data.inputValue,
          };

          return {
            ...opt,
            isSelected: true,
            input,
          };
        }

        return {
          ...opt,
          isSelected: false,
        };
      });

      const updatedQuestion: ConditionQuestion = {
        ...question,
        options: updatedOptions,
      };

      updateAnswer(intakeFormQuestionId, updatedQuestion);
      handleNextStep();
    } catch {
      // @TODO: Handle error
      setIsLoading(false);
    }
  }

  return (
    <form className="flex flex-1 flex-col" onSubmit={handleSubmit(onSubmit)}>
      <div className="grid gap-2">
        <Field error={Boolean(errors.option)}>
          <Label>{question.question}</Label>

          <ErrorMessage>{errors.option?.message}</ErrorMessage>

          <Controller
            control={control}
            name="option"
            render={(x) => {
              const { defaultValues } = x.formState;
              const defaultValue = defaultValues?.option;

              const handleOnChange = (value: string) => {
                setValue('option', value);
                clearErrors('inputValue');

                const selectedOptionInput = options.find(
                  ({ name }) => name === value,
                )?.input;

                if (!selectedOptionInput) {
                  setValue('inputValue', null);
                }
              };

              return (
                <RadioGroup
                  defaultValue={defaultValue}
                  onChange={handleOnChange}
                >
                  {options.map((opt) => (
                    <Field key={opt.name}>
                      <Radio value={opt.name} />
                      <Label>{opt.name}</Label>
                    </Field>
                  ))}
                </RadioGroup>
              );
            }}
            rules={{
              required: {
                value: question.isRequired,
                message: 'Please select an option',
              },
            }}
          />
        </Field>

        {conditionalInput && (
          <Field error={Boolean(errors.inputValue)}>
            <Label>Please provide details</Label>

            <ErrorMessage>{errors.inputValue?.message}</ErrorMessage>

            <Textarea
              autoFocus
              className="mb-1"
              placeholder="Type your response..."
              {...register('inputValue', {
                required: {
                  value: conditionalInput.isRequired,
                  message: 'Please type a response',
                },
                maxLength: {
                  value: INPUT_MAX_LENGTH,
                  message: `Maximum length is ${INPUT_MAX_LENGTH} characters`,
                },
              })}
            />
            <Paragraph
              className={cn('text-right font-semibold', {
                'text-text-error': Boolean(errors.inputValue),
              })}
              size="xs"
            >
              {inputValue?.length || 0}/{INPUT_MAX_LENGTH}
            </Paragraph>
          </Field>
        )}
      </div>

      <SubmitButton isLoading={isLoading} />
    </form>
  );
}
